import { Check, Eye, EyeOff, Loader2, Pencil, RotateCw, X } from "@fg/icons";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { fileKind, formatBytes } from "@/lib/drive";
import { cn } from "@/lib/utils";
import { FileTypeIcon } from "./DriveFileCard";
import { PERMISSION_LABELS, type DriveAttachments, type PendingFile, type SharePermission } from "./useDriveAttachments";

const PERMISSIONS: SharePermission[] = ["edit", "view", "view-no-download"];

const PERMISSION_SHORT: Record<SharePermission, string> = {
    edit: "Can edit",
    view: "Can view",
    "view-no-download": "View only"
};

function PermissionIcon({ permission, className }: { permission: SharePermission; className?: string }) {
    if (permission === "edit") return <Pencil className={className} aria-hidden />;
    if (permission === "view") return <Eye className={className} aria-hidden />;
    return <EyeOff className={className} aria-hidden />;
}

/**
 * The pending-file chips above a composer: type icon, name, size, upload
 * progress, a per-file permission menu and remove / cancel. Errors stay on
 * the chip (with Retry) so a failed upload is never silent.
 */
export function ComposerAttachments({ attachments }: { attachments: DriveAttachments }) {
    if (attachments.files.length === 0) return null;
    return (
        <ul className="flex flex-wrap gap-1.5" aria-label="Attached files">
            {attachments.files.map((f) => (
                <AttachmentChip key={f.key} file={f} attachments={attachments} />
            ))}
        </ul>
    );
}

function AttachmentChip({ file, attachments }: { file: PendingFile; attachments: DriveAttachments }) {
    const kind = fileKind(file.name, file.type);
    const pct = Math.round(file.progress * 100);
    const failed = file.status === "error";
    return (
        <li
            className={cn(
                "relative flex max-w-full items-center gap-2 overflow-hidden rounded-md border bg-card py-1.5 pl-2 pr-1 text-xs shadow-sm sm:max-w-[20rem]",
                failed && "border-destructive/60 bg-destructive/5"
            )}
        >
            <FileTypeIcon kind={kind} className="h-5 w-5 shrink-0" />
            <div className="min-w-0 flex-1">
                <div className="truncate font-medium" title={file.name}>{file.name}</div>
                <div className={cn("truncate text-[10px]", failed ? "text-destructive" : "text-muted-foreground")} title={file.error}>
                    {failed
                        ? file.error ?? "Upload failed"
                        : file.status === "uploading"
                            ? `${formatBytes(file.size)} · Uploading ${pct}%`
                            : file.locked
                                ? `${formatBytes(file.size)} · Sending…`
                                : formatBytes(file.size)}
                </div>
            </div>
            {failed && !file.locked ? (
                <button
                    type="button"
                    onClick={() => attachments.retry(file.key)}
                    aria-label={`Retry uploading ${file.name}`}
                    title="Retry"
                    className="flex h-6 w-6 shrink-0 items-center justify-center rounded hover:bg-accent"
                >
                    <RotateCw className="h-3.5 w-3.5" />
                </button>
            ) : null}
            {!failed ? (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button
                            type="button"
                            disabled={file.locked}
                            aria-label={`Sharing for ${file.name}: ${PERMISSION_LABELS[file.permission]}. Change`}
                            title={PERMISSION_LABELS[file.permission]}
                            className="flex h-6 shrink-0 items-center gap-1 rounded px-1.5 text-[10px] text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50"
                        >
                            <PermissionIcon permission={file.permission} className="h-3 w-3" />
                            <span className="hidden sm:inline">{PERMISSION_SHORT[file.permission]}</span>
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-64">
                        <DropdownMenuLabel className="text-xs text-muted-foreground">People in this conversation</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        {PERMISSIONS.map((p) => (
                            <DropdownMenuItem key={p} onSelect={() => attachments.setPermission(file.key, p)} className="text-xs">
                                <PermissionIcon permission={p} className="mr-2 h-3.5 w-3.5" />
                                <span className="flex-1">{PERMISSION_LABELS[p]}</span>
                                {file.permission === p ? <Check className="ml-2 h-3.5 w-3.5 text-primary" aria-label="Selected" /> : null}
                            </DropdownMenuItem>
                        ))}
                    </DropdownMenuContent>
                </DropdownMenu>
            ) : null}
            {file.locked ? (
                <Loader2 className="mx-1 h-3.5 w-3.5 shrink-0 animate-spin text-muted-foreground" aria-label="Sending" />
            ) : (
                <button
                    type="button"
                    onClick={() => attachments.remove(file.key)}
                    aria-label={file.status === "uploading" ? `Cancel upload of ${file.name}` : `Remove ${file.name}`}
                    title={file.status === "uploading" ? "Cancel upload" : "Remove"}
                    className="flex h-6 w-6 shrink-0 items-center justify-center rounded hover:bg-accent"
                >
                    <X className="h-3.5 w-3.5" />
                </button>
            )}
            {file.status === "uploading" ? (
                <span
                    className="absolute bottom-0 left-0 h-0.5 bg-primary transition-[width]"
                    style={{ width: `${Math.max(3, pct)}%` }}
                    role="progressbar"
                    aria-label={`Uploading ${file.name}`}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={pct}
                />
            ) : null}
        </li>
    );
}

/** Full-area overlay shown while files are dragged over a thread or composer. */
export function DropOverlay({ label = "Drop files to share" }: { label?: string }) {
    return (
        <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center rounded-md border-4 border-dashed border-primary bg-primary/5 text-sm font-semibold text-primary backdrop-blur-[1px]">
            {label}
        </div>
    );
}
